import { useEffect, useRef, useState } from "react";
import { Search, Pill } from "lucide-react";
import { api } from "@/lib/api";

export const SPECIALTY_STYLES = {
    "General Medicine": "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Pediatrics": "bg-sky-50 text-sky-800 border-sky-200",
    "Orthopedics": "bg-amber-50 text-amber-800 border-amber-200",
    "Cardiology": "bg-rose-50 text-rose-800 border-rose-200",
    "Dermatology": "bg-purple-50 text-purple-800 border-purple-200",
    "ENT": "bg-indigo-50 text-indigo-800 border-indigo-200",
    "Gynecology": "bg-pink-50 text-pink-800 border-pink-200",
    "Gastroenterology": "bg-orange-50 text-orange-800 border-orange-200",
    "Pulmonology": "bg-teal-50 text-teal-800 border-teal-200",
    "Endocrinology": "bg-cyan-50 text-cyan-800 border-cyan-200",
    "Psychiatry": "bg-violet-50 text-violet-800 border-violet-200",
    "Neurology": "bg-blue-50 text-blue-800 border-blue-200",
    "Ophthalmology": "bg-lime-50 text-lime-800 border-lime-200",
    "Dental": "bg-stone-100 text-stone-800 border-stone-300",
    "Urology": "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
};

export default function MedicineSearch({ onSelect }) {
    const [q, setQ] = useState("");
    const [specialty, setSpecialty] = useState("Gynecology");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const boxRef = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/medicines", { params: { q, specialty } });
                setResults(data);
                setOpen(true);
            } catch { setResults([]); }
            finally { setLoading(false); }
        }, 200);
        return () => clearTimeout(timer.current);
    }, [q, specialty]);

    return (
        <div ref={boxRef} className="relative" data-testid="medicine-search">
            <div className="flex gap-1.5 flex-wrap mb-2">
                {["All", "Gynecology", "General Medicine", "Endocrinology", "Gastroenterology", "Urology", "Dermatology", "Pediatrics", "ENT"].map((s) => (
                    <button
                        key={s}
                        type="button"
                        data-testid={`specialty-tab-${s.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setSpecialty(s)}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-150 ${
                            specialty === s ? "bg-[#E06D1F] text-white border-[#E06D1F]" : (SPECIALTY_STYLES[s] || "bg-slate-50 text-slate-600 border-slate-200") + " hover:border-emerald-300"
                        }`}
                    >
                        {s}
                    </button>
                ))}
            </div>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                    data-testid="medicine-autocomplete-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => setOpen(true)}
                    placeholder="Search medicine by brand or generic name..."
                    className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none"
                />
            </div>
            {open && (
                <div data-testid="medicine-results-dropdown" className="absolute z-20 mt-1.5 w-full max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg animate-fade-up">
                    {loading && <p className="px-4 py-3 text-sm text-slate-500">Searching...</p>}
                    {!loading && results.length === 0 && (
                        <div className="px-4 py-3">
                            <p className="text-sm text-slate-500">No medicines found.</p>
                            {q.trim() && (
                                <button type="button" data-testid="medicine-add-custom" onClick={() => { onSelect({ name: q.trim(), generic: "", form: "", strength: "", specialty: "" }); setQ(""); setOpen(false); }} className="mt-1 text-sm font-medium text-[#E06D1F] hover:underline">
                                    + Add "{q.trim()}" as custom medicine
                                </button>
                            )}
                        </div>
                    )}
                    {!loading && results.map((m) => (
                        <button
                            key={m.id}
                            type="button"
                            data-testid={`medicine-option-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => { onSelect(m); setQ(""); setOpen(false); }}
                            className="w-full text-left px-4 py-2.5 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0 transition-colors duration-100"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Pill className="h-3.5 w-3.5 text-[#E06D1F]" /> {m.name}
                                </span>
                                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${SPECIALTY_STYLES[m.specialty] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{m.specialty}</span>
                            </div>
                            <p className="mt-0.5 text-xs text-slate-500">{m.form}{m.strength ? ` · ${m.strength}` : ""} · {m.generic}</p>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
